import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  GraduationCap, Target, NotebookPen, MessageSquareReply,
  UserPlus, Users, Sparkles, Activity, Clock, ChevronRight,
} from "lucide-react";

const STAT_META = [
  { key: "total_students", label: "Öğrenci", icon: GraduationCap, color: "from-violet-500 to-violet-600" },
  { key: "active_bep", label: "Aktif BEP Hedefi", icon: Target, color: "from-blue-500 to-blue-600" },
  { key: "today_notes", label: "Bugünün Notları", icon: NotebookPen, color: "from-fuchsia-500 to-pink-600" },
  { key: "pending_feedback", label: "Bekleyen Bildirim", icon: MessageSquareReply, color: "from-amber-500 to-orange-600" },
];

const ACT_ICON = {
  student: GraduationCap, user: UserPlus, bep: Target, note: NotebookPen, feedback: MessageSquareReply,
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  return d.toLocaleDateString("tr-TR");
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isTeacher = user?.role === "teacher";
  const [stats, setStats] = useState(null);
  const [acts, setActs] = useState([]);
  const [myStudents, setMyStudents] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/activities?limit=10").then((r) => setActs(r.data)).catch(() => {});
    if (isTeacher) api.get("/students").then((r) => setMyStudents(r.data)).catch(() => {});
  }, [isTeacher]);

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="dashboard-page">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">{isTeacher ? "Öğretmen Paneli" : "Yönetim Paneli"}</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Hoş geldin, {user?.name?.split(" ")[0] || (isTeacher ? "Öğretmen" : "Admin")} 👋
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {isTeacher ? "Öğrencilerinizin gelişimini buradan takip edin." : "Renka Özel Eğitim Merkezi merkezi yönetim sistemi."}
          </p>
        </header>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STAT_META.map(({ key, label, icon: Icon, color }) => (
            <Card key={key} data-testid={`stat-${key}`}
              className="rounded-2xl border-slate-200/60 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.03)]">
              <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${color}`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats ? stats[key] ?? 0 : "—"}</p>
              <p className="mt-1 text-sm text-slate-500">{isTeacher && key === "total_students" ? "Öğrencim" : label}</p>
            </Card>
          ))}
        </div>

        {isTeacher ? (
          <Card className="rounded-2xl border-slate-200/60 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="teacher-students">
            <div className="mb-4 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-violet-600" />
              <h2 className="font-heading text-lg font-semibold text-slate-800">Öğrencilerim</h2>
            </div>
            {myStudents.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">Henüz size atanmış bir öğrenci yok. Lütfen kurum yöneticinizle iletişime geçin.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {myStudents.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => navigate(`/adminxx1/bep?student=${s.id}`)}
                    data-testid={`teacher-student-${s.id}`}
                    className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-600">
                      {(s.full_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800">{s.full_name}</p>
                      {s.diagnosis && <p className="truncate text-xs text-slate-400">{s.diagnosis}</p>}
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </button>
                ))}
              </div>
            )}
          </Card>
        ) : (
          <Card className="rounded-2xl border-slate-200/60 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="recent-activities">
            <div className="mb-4 flex items-center gap-2">
              <Activity className="h-5 w-5 text-violet-600" />
              <h2 className="font-heading text-lg font-semibold text-slate-800">Son Aktiviteler</h2>
            </div>
            {acts.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">Henüz aktivite yok.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {acts.map((a, i) => {
                  const Icon = ACT_ICON[a.type] || Sparkles;
                  return (
                    <div key={i} className="flex items-center gap-3 py-3" data-testid={`activity-${i}`}>
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                        <Icon className="h-4 w-4" />
                      </div>
                      <p className="flex-1 truncate text-sm text-slate-700">{a.text}</p>
                      <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" />{timeAgo(a.date)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
